import { useEffect, useRef } from "react";
import { useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import {
  DEFAULT_THEME_PREFERENCE,
  loadStoredThemePreferenceForUser,
  sanitizeThemePreference,
  type ThemePreference,
} from "../styles/themePalette";
import { takeLoginThemeIfAny } from "../lib/themeLoginBootstrap";
import { apiGetMeTheme, apiPatchMeTheme } from "../lib/themePreferenceApi";

function themePreferenceEqual(a: string, b: string): boolean {
  try {
    return JSON.stringify(JSON.parse(a)) === JSON.stringify(JSON.parse(b));
  } catch {
    return a === b;
  }
}

function themeMatchesDefaultConcrete(pref: ThemePreference): boolean {
  return (
    JSON.stringify(sanitizeThemePreference(pref)) === JSON.stringify(DEFAULT_THEME_PREFERENCE)
  );
}

/** Binds theme to the logged-in account: server sync + debounced saves. */
export function ThemeAccountSync() {
  const { identityRow } = useAuth();
  const { accountUserId, setAccountUserId, preference, setThemePreference } = useTheme();
  const lastPushedJson = useRef<string | null>(null);
  const pushTimer = useRef<number | null>(null);

  useEffect(() => {
    const id = identityRow?.userId ?? null;
    setAccountUserId(id);
  }, [identityRow?.userId, setAccountUserId]);

  useEffect(() => {
    const uid = identityRow?.userId;
    if (!uid) return;

    let cancelled = false;
    void (async () => {
      const fromLogin = takeLoginThemeIfAny();
      if (fromLogin !== undefined) {
        const normalized = sanitizeThemePreference(fromLogin);
        if (!cancelled) {
          setThemePreference(normalized);
          lastPushedJson.current = JSON.stringify(normalized);
        }
        return;
      }
      try {
        const server = await apiGetMeTheme();
        if (cancelled) return;
        const serverJson = JSON.stringify(server);
        const localCached = loadStoredThemePreferenceForUser(uid);
        const localJson = JSON.stringify(localCached);
        if (!themePreferenceEqual(serverJson, localJson)) {
          // DB default is Concrete for all accounts; if the client still has a non-default
          // theme (e.g. legacy localStorage only), do not overwrite with server default —
          // the debounced PATCH below will push local to the server.
          if (themeMatchesDefaultConcrete(server) && !themeMatchesDefaultConcrete(localCached)) {
            /* keep React state + local cache */
          } else {
            setThemePreference(server);
          }
        }
        lastPushedJson.current = serverJson;
      } catch {
        /* offline or error — keep local / cached preference */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [identityRow?.userId, setThemePreference]);

  useEffect(() => {
    if (!accountUserId) {
      if (pushTimer.current != null) {
        window.clearTimeout(pushTimer.current);
        pushTimer.current = null;
      }
      lastPushedJson.current = null;
      return;
    }

    const nextJson = JSON.stringify(preference);
    if (lastPushedJson.current !== null && themePreferenceEqual(lastPushedJson.current, nextJson)) {
      return;
    }

    if (pushTimer.current != null) {
      window.clearTimeout(pushTimer.current);
    }
    pushTimer.current = window.setTimeout(() => {
      pushTimer.current = null;
      void (async () => {
        try {
          const { theme } = await apiPatchMeTheme(preference);
          lastPushedJson.current = JSON.stringify(theme);
        } catch {
          /* retry on next preference change */
        }
      })();
    }, 600);

    return () => {
      if (pushTimer.current != null) {
        window.clearTimeout(pushTimer.current);
        pushTimer.current = null;
      }
    };
  }, [accountUserId, preference]);

  return null;
}
